import { useQuery } from '@tanstack/react-query';
import { FC, useEffect } from 'react';
import { matchPath, Navigate, Outlet, useLocation, useNavigation } from 'react-router-dom';

import { _getQuestionnaireStatus } from '@/api/auth/queries';
import { useGetUserProfile } from '@/api/user-dashboard/queries';
import LogoMark from '@/assets/svg/logos/logo-mark.svg';
import {
  buildUrlWithUtmParams,
  getUtmParamsFromSearch,
  storeAuthAttributionFromSearch,
} from '@/features/auth';
import { isSignUpPageHeld } from '@/features/auth/sign-up-page-hold';
import { needsQuestionnaire } from '@/features/profile-requirements/needs-questionnaire';
import { PagePaths } from '@/routes/page-paths';
import { Permission, useCan } from '@/shared/modules/permissions';

import { GeneralLoadingFallback } from '../general-loading-fallback';
import { PromoPanel } from './promo-panel';
import { HeaderLogoLink, MainWrapper, MobileHeader, SkipLink, Wrapper } from './styled';

export const AuthLayout: FC = () => {
  const location = useLocation();
  const navigation = useNavigation();

  const { user, isPending } = useGetUserProfile();
  const canActAcrossEntities = useCan(Permission.GlobalAdminCrossEntityAccess);
  const {
    data: status,
    isPending: isStatusPending,
    isError: isStatusError,
  } = useQuery({
    ..._getQuestionnaireStatus(),
    enabled: !!user && user.hasSubmittedQuestionnaire,
  });

  useEffect(() => {
    storeAuthAttributionFromSearch(location.search);
  }, [location.search]);

  // The personal-QR connect link (GP-3960) must reach its own page component even for a signed-in
  // visitor, so it can hand the token to the address book instead of being bounced here first.
  // Matched with `matchPath` rather than an exact-string compare because `qrConnect` is a dynamic
  // `:token` path.
  const isQrConnect = matchPath(PagePaths.qrConnect, location.pathname) !== null;
  const isPasswordRecovery =
    location.pathname === PagePaths.forgotPassword ||
    (location.pathname === PagePaths.signUp && isSignUpPageHeld());
  const redirectTo = (path: string) =>
    buildUrlWithUtmParams(path, getUtmParamsFromSearch(location.search));

  // Recovery never redirects, and its automatic log in reloads the profile mid-flow; a fallback
  // here would unmount the page and drop its step state.
  if (isPending && !isPasswordRecovery) {
    return <GeneralLoadingFallback />;
  }

  if (user && !isQrConnect && !isPasswordRecovery) {
    // Session-cache refresh remounts this layout while the completion destination still loads.
    // A second redirect would discard its stored attribution or safe return intent.
    if (navigation.state !== 'idle') return <GeneralLoadingFallback />;
    if (canActAcrossEntities) {
      return <Navigate to={redirectTo(PagePaths.allFunds)} replace />;
    }
    if (!user.hasSubmittedQuestionnaire) {
      return <Navigate to={redirectTo(PagePaths.questionnaire)} replace />;
    }
    if (isStatusPending) {
      return <GeneralLoadingFallback />;
    }
    if (!isStatusError && status && needsQuestionnaire(status)) {
      return <Navigate to={redirectTo(PagePaths.questionnaire)} replace />;
    }
    return <Navigate to={redirectTo(PagePaths.allFunds)} replace />;
  }

  return (
    <Wrapper>
      <SkipLink href="#auth-main">Skip to authentication form</SkipLink>
      <PromoPanel />
      <MobileHeader>
        <HeaderLogoLink to={PagePaths.root} aria-label="Return to sign in">
          <LogoMark role="img" aria-label="Gamma Prime" focusable="false" />
        </HeaderLogoLink>
      </MobileHeader>
      <MainWrapper id="auth-main" tabIndex={-1}>
        <Outlet />
      </MainWrapper>
    </Wrapper>
  );
};
